////////////////////////////////////////////
// useSolWallet — THE APP'S SOLANA WALLET SURFACE
////////////////////////////////////////////
//
// WRAPS wallet-adapter'S useWallet IN THE SHAPE THE REST OF THE APP USES:
// BASE58 STRING AS SOURCE OF TRUTH, sign(txBase64) FOR THE SERVER-BUILT TX
// FLOW, signMessage FOR SIGN-IN, AND THE users/seen + SESSION PINGS THE
// ADMIN FUNNEL DEPENDS ON. ON MOBILE A PHANTOM/SOLFLARE DEEPLINK SESSION
// (lib/wallet/deeplink) COUNTS AS CONNECTED TOO — THE USER STAYS IN THEIR
// NATIVE BROWSER AND THE WALLET APP ANSWERS SIGN REQUESTS VIA REDIRECTS.
import { useCallback, useEffect, useMemo, useRef, useSyncExternalStore } from "react";
import { useWallet } from "@solana/wallet-adapter-react";
import { Transaction, VersionedTransaction } from "@solana/web3.js";
import * as log from "@/lib/log";
import { postJson } from "@/lib/api/client";
import { clearDlSession, dlSession, subscribeDl } from "@/lib/wallet/deeplink/store";

// A SERVER-BUILT TX ARRIVES AS EITHER WIRE SHAPE — LEGACY (Transaction) OR v0
// (VersionedTransaction, THE PUMP INSTANT FLOW ONCE THE ALT LANDED — SEE
// docs/pump-instant-alt-plan.md U-A3/U-A4). NOTE THE GOTCHA A NAIVE
// try/catch WOULD MISS: VersionedTransaction.deserialize NEVER THROWS ON
// LEGACY BYTES IN THIS web3.js VERSION — IT GRACEFULLY WRAPS A LEGACY
// Message (WHICH DUCK-TYPES staticAccountKeys/compiledInstructions FOR
// INTEROP, SEE VersionedMessage.deserialize's 'legacy' BRANCH). THAT'S FINE
// FOR THE SERVER'S relayInstructions/feePayerOf (web/server/services/
// tx.service.mjs), WHICH ONLY EVER READS THOSE UNIFORM ACCESSORS — BUT THE
// CLIENT MUST HAND wallet-adapter A REAL Transaction INSTANCE FOR A LEGACY
// TX, NOT A VersionedTransaction WRAPPING ONE. SO DETECT VIA THE LIBRARY'S
// OWN DISCRIMINATOR INSTEAD: message.version IS THE STRING 'legacy' FOR A
// LEGACY MESSAGE, 0 FOR A v0 ONE (VERIFIED AGAINST THE INSTALLED PACKAGE —
// SEE THE U-A4 GREEN-LIGHT SCRIPT). LEGACY STAYS THE DEFAULT, WELL-TRODDEN
// PATH: ONLY A CONFIRMED v0 MESSAGE TAKES THE VersionedTransaction BRANCH.
function deserializeTx(txBase64: string): Transaction | VersionedTransaction {

  const buf = Uint8Array.from(atob(txBase64), (c) => c.charCodeAt(0));
  const v = VersionedTransaction.deserialize(buf);
  return v.version === "legacy" ? Transaction.from(buf) : v;
}

// BASE64 OUT — WORKS FOR BOTH SHAPES (Transaction#serialize() → Buffer,
// VersionedTransaction#serialize() → Uint8Array; BOTH ARE ARRAY-LIKE HERE).
function serializeTx(tx: Transaction | VersionedTransaction): string {

  return btoa(String.fromCharCode(...tx.serialize()));
}

// MOBILE UA — PHONES HAVE NO EXTENSIONS; WALLET APPS ANSWER VIA DEEPLINK
export function isMobileUA(): boolean {

  return /android|iphone|ipad|ipod/i.test(navigator.userAgent) ||
    // IPADOS REPORTS A MAC UA; TOUCH POINTS GIVE IT AWAY
    (/Mac/.test(navigator.platform) && navigator.maxTouchPoints > 1);
}

// SEEN PING — SEED A users ROW + REFRESH THE SESSION FUNNEL, ONCE PER WALLET
// PER TAB (MIRRORS wallet.js pingSeen; fuze:seen SESSION KEY)
function pingSeen(wallet: string) {

  try {
    if (sessionStorage.getItem("fuze:seen") === wallet) return;
    sessionStorage.setItem("fuze:seen", wallet);
  } catch { /* SESSIONSTORAGE BLOCKED — STILL PING BELOW */ }

  postJson("/api/users/seen", { wallet }).catch(() => { /* FIRE-AND-FORGET */ });
  log.pingSession();
}

export function useSolWallet() {

  const w = useWallet();

  // DEEPLINK SESSION — LIVE VIA THE EXTERNAL STORE; COUNTS AS CONNECTED
  // WHENEVER NO ADAPTER IS (NO NET GATING — WHATEVER WALLET IS CONNECTED IS
  // THE WALLET; THE WALLET APP ITSELF REPORTS ANYTHING IT CAN'T SIGN)
  const dl = useSyncExternalStore(subscribeDl, dlSession);
  const dlActive = !!dl && !w.publicKey;
  const pubkey = w.publicKey ? w.publicKey.toBase58() : dlActive ? dl.pubkey : null;

  // AN ADAPTER CONNECT SUPERSEDES ANY DEEPLINK SESSION — ONE SOURCE OF TRUTH
  useEffect(() => {

    if (w.publicKey && dl)
      clearDlSession();
  }, [w.publicKey, dl]);

  // CONNECT/DISCONNECT BREADCRUMBS + SEEN PING — ON ADDRESS TRANSITIONS
  const prevRef = useRef<string | null>(null);
  useEffect(() => {

    const prev = prevRef.current;
    if (pubkey && pubkey !== prev) {

      log.info("wallet", prev ? "account changed" : "connected", { wallet: shortKey(pubkey) });
      pingSeen(pubkey);
    }
    if (!pubkey && prev)
      log.info("wallet", "disconnected", { wallet: shortKey(prev) });
    prevRef.current = pubkey;
  }, [pubkey]);

  // SIGN — DESERIALIZE THE SERVER-BUILT TX (LEGACY OR v0, SEE deserializeTx),
  // SIGN IN THE WALLET, RETURN BASE64. A DEEPLINK SESSION NEVER REACHES THIS —
  // buildSignSubmit ROUTES IT THROUGH THE WALLET-APP REDIRECT INSTEAD. THE
  // STEP-BY-STEP FALLBACK (NO signAll) ALSO CALLS sign() ONE TX AT A TIME, SO
  // THIS PATH MUST HANDLE v0 TOO — FALLBACK WALLETS GET THE SAME v0 PAYLOADS.
  const sign = useCallback(async (txBase64: string): Promise<string> => {

    if (!w.signTransaction) throw new Error("connect a wallet first");

    const tx = deserializeTx(txBase64);
    try {
      const signed = await w.signTransaction(tx);
      return serializeTx(signed);
    } catch (e) {
      // USER CANCEL IS EXPECTED, NOT AN ERROR — CLASSIFY FOR THE LOGS
      const err = e as { code?: number; message?: string };
      const cancelled = err && (err.code === 4001 || /reject|cancel|denied/i.test(err.message || ""));
      if (cancelled) log.info("wallet", "sign cancelled", { code: err.code });
      else log.error("wallet", "sign failed", { err: err?.message, code: err?.code });
      throw e;
    }
  }, [w]);

  // SIGN ALL — SAME DESERIALIZE/SIGN/SERIALIZE SHAPE AS sign(), FOR A WHOLE
  // SIGNING SESSION IN ONE WALLET POPUP (wallet-adapter signAllTransactions —
  // the instant-create one-approval flow). PRESERVES ANY SERVER-SIDE PARTIAL
  // SIGNATURES (THE MINT CO-SIGN ON TX A), SAME AS signTransaction DOES. THE
  // BATCH IS NOT ASSUMED HOMOGENEOUS — EACH TX IS DETECTED INDEPENDENTLY, SO
  // A MIX OF LEGACY AND v0 TXS IN ONE CALL IS HANDLED (TODAY'S INSTANT
  // SESSION IS ALL-v0, BUT NOTHING HERE RELIES ON THAT).
  const signAll = useCallback(async (txsBase64: string[]): Promise<string[]> => {

    if (!w.signAllTransactions) throw new Error("this wallet can't sign a batch — use the step-by-step flow");

    const txs = txsBase64.map((b64) => deserializeTx(b64));
    try {
      const signed = await w.signAllTransactions(txs);
      return signed.map((t) => serializeTx(t));
    } catch (e) {
      const err = e as { code?: number; message?: string };
      const cancelled = err && (err.code === 4001 || /reject|cancel|denied/i.test(err.message || ""));
      if (cancelled) log.info("wallet", "signAll cancelled", { code: err.code });
      else log.error("wallet", "signAll failed", { err: err?.message, code: err?.code });
      throw e;
    }
  }, [w]);

  // SIGN MESSAGE — UTF-8 BYTES IN, BASE64 SIGNATURE OUT (THE AUTH SHAPE)
  const signMessage = useCallback(async (message: string): Promise<string> => {

    if (!w.signMessage) throw new Error("connect a wallet first");

    try {
      const sig = await w.signMessage(new TextEncoder().encode(message));
      return btoa(String.fromCharCode(...sig));
    } catch (e) {
      const err = e as { code?: number; message?: string };
      const cancelled = err && (err.code === 4001 || /reject|cancel|denied/i.test(err.message || ""));
      if (cancelled) log.info("wallet", "sign-in cancelled", { code: err.code });
      else log.error("wallet", "sign message failed", { err: err?.message, code: err?.code });
      throw e;
    }
  }, [w]);

  // DISCONNECT — DROPS WHICHEVER PATH IS LIVE (ADAPTER AND/OR DEEPLINK SESSION)
  const disconnect = useCallback(async () => {

    clearDlSession();
    await w.disconnect();
  }, [w]);

  return useMemo(() => ({
    // BASE58 STRING IS THE SOURCE OF TRUTH APP-WIDE
    pubkey,
    short: shortKey(pubkey),
    connected: !!pubkey,
    // CONNECTED THROUGH A WALLET-APP DEEPLINK SESSION (NATIVE MOBILE BROWSER)
    dlActive,
    // WALLET-STANDARD WALLETS AVAILABLE RIGHT NOW (EMPTY ON A BARE PHONE);
    // ANDROID GAINS THE MOBILE WALLET ADAPTER ENTRY ONCE registerMwa() RAN
    wallets: w.wallets,
    // SELECTED WALLET — LET THE CONTROLLER CONNECT ONLY ONCE THE SELECTION
    // HAS LANDED (RACE-FREE: select() IS ASYNC STATE, connect() READS IT)
    wallet: w.wallet,
    select: w.select,
    connect: w.connect,
    disconnect,
    connecting: w.connecting,
    sign,
    // undefined WHEN THE PATH CAN'T BATCH-SIGN — A DEEPLINK SESSION (NO
    // ADAPTER) OR AN ADAPTER WITHOUT signAllTransactions. CALLERS BRANCH ON
    // PRESENCE AND FALL BACK TO THE ONE-TX-AT-A-TIME FLOW.
    signAll: w.signAllTransactions && !dlActive ? signAll : undefined,
    signMessage,
  }), [pubkey, dlActive, w.wallets, w.wallet, w.select, w.connect, disconnect, w.connecting, sign, signAll, signMessage, w.signAllTransactions]);
}

function shortKey(k: string | null): string | null {

  return k ? k.slice(0, 4) + "…" + k.slice(-4) : null;
}
